// ── La verification d'une proposition du modele, et sa traduction pour l'editeur (chantier COP, C3, C8) ──
// Pur : rien ne touche au projet. Soit la proposition est acceptee (son apercu, ses etapes, ses descriptions), soit elle
// est refusee avec sa raison, en francais, que le modele reprend pour l'utilisateur.
import { cmToPlanPx } from '@/lib/rooms/catalog';
import type { CopiloteAction } from '@/lib/schemas/copilote.schema';
import type { Opening, Point, ProjectState, WindowStyle } from '@/lib/types';
import { WINDOW_CATALOG } from '@/lib/windows/catalog';
import { onFloor, pxToCm } from './resume';

const MAX_WALL_CM = 3000;
const THICKNESS_CM = { min: 5, max: 50 };
const DEFAULT_THICKNESS = { exterior: 20, partition: 7 } as const;
/** La zone du plan : les murs de l'etage, elargis de 5 m */
const PLAN_MARGIN_CM = 500;
const MIN_ROOM_M2 = 1;
/** Un cote de piece est deja un mur si un mur le suit a moins de 15 cm, sur 95 % de sa longueur */
const SIDE_TOLERANCE_CM = 15;
const SIDE_COVERED = 0.95;
const SAME_WALL_CM = 5;
const OPENING_WIDTH_CM = { min: 30, max: 600 };
const STYLES: Record<'porte' | 'fenetre' | 'baie', { style: WindowStyle; type: Opening['type']; name: string }> = {
  porte: { style: 'door-interior', type: 'door', name: 'porte' },
  fenetre: { style: 'casement', type: 'window', name: 'fenêtre' },
  baie: { style: 'sliding-2', type: 'window', name: 'baie vitrée' },
};

export type Etape =
  | { type: 'murs'; a: Point; b: Point; wallType: 'exterior' | 'partition'; thickness: number }
  | { type: 'ouverture'; opening: Omit<Opening, 'id'> }
  | { type: 'supprimer'; ids: string[] }
  | { type: 'piece'; nom: string; point: Point };

export interface SegmentApercu {
  kind: 'ajout' | 'suppression';
  a: Point;
  b: Point;
  label?: string;
}

export interface EtapesOk {
  ok: true;
  etapes: Etape[];
  apercu: { segments: SegmentApercu[]; textes: { at: Point; text: string }[] };
  /** Une ligne par action, pour la carte de la proposition */
  descriptions: string[];
  /** Pour l'etiquette sur le plan */
  titre: string;
  /** Le nombre d'actions : « Appliquer les 2 actions » */
  nombre: number;
  /** Rien que des suppressions : le bouton dit « Supprimer » (C11) */
  boutonSupprimer: boolean;
}
export type Preparation = EtapesOk | { ok: false; raison: string };

const meters = (cm: number) => `${(cm / 100).toFixed(2).replace('.', ',')} m`;

class Refus extends Error {}

export function preparer(p: ProjectState, etageId: string, actions: CopiloteAction[], ids: Record<string, string>): Preparation {
  const cal = p.calibration;
  const px = (cm: number) => cmToPlanPx(cm, cal);
  const toPx = (q: { x: number; y: number }): Point => ({ x: px(q.x), y: px(q.y) });
  const toCm = (v: number) => pxToCm(v, cal);
  const walls = p.walls.filter(onFloor(p, etageId));

  // La zone du plan, en cm ; sans mur, rien a comparer
  const xs = walls.flatMap(w => [w.start.x, w.end.x]).map(toCm);
  const ys = walls.flatMap(w => [w.start.y, w.end.y]).map(toCm);
  const inPlan = (q: { x: number; y: number }) => walls.length === 0 || (
    q.x >= Math.min(...xs) - PLAN_MARGIN_CM && q.x <= Math.max(...xs) + PLAN_MARGIN_CM
    && q.y >= Math.min(...ys) - PLAN_MARGIN_CM && q.y <= Math.max(...ys) + PLAN_MARGIN_CM);

  const wallOf = (shortId: string) => {
    const wall = walls.find(w => w.id === ids[shortId]);
    if (!wall) throw new Refus(`Le mur ${shortId} n'existe pas sur cet étage.`);
    return wall;
  };

  const etapes: Etape[] = [];
  // Dans une meme proposition : les ouvertures deja posees, les murs supprimes
  const proposed: { wallId: string; center: number; width: number }[] = [];
  const deleted = new Set<string>();
  const openedWalls = new Set<string>();
  const segments: SegmentApercu[] = [];
  const textes: { at: Point; text: string }[] = [];
  const descriptions: string[] = [];
  let titre = '';
  const setTitre = (t: string) => { if (!titre) titre = t; };

  // Le modele propose parfois deux fois le meme mur (ajouter_murs et creer_piece) : une seule fois suffit
  const seen: { a: { x: number; y: number }; b: { x: number; y: number } }[] = [];
  const close = (u: { x: number; y: number }, v: { x: number; y: number }) => Math.hypot(u.x - v.x, u.y - v.y) < SAME_WALL_CM;
  const already = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    seen.some(s => (close(s.a, a) && close(s.b, b)) || (close(s.a, b) && close(s.b, a)));

  const addWall = (a: { x: number; y: number }, b: { x: number; y: number }, wallType: 'exterior' | 'partition', epaisseur?: number) => {
    if (already(a, b)) return null;
    seen.push({ a, b });
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 1) throw new Refus('Un mur doit avoir une longueur.');
    if (len > MAX_WALL_CM) throw new Refus(`Un mur de ${meters(len)} est trop long : 30 m au plus.`);
    if (!inPlan(a) || !inPlan(b)) throw new Refus(`Ce mur de ${meters(len)} tomberait hors du plan.`);
    const thickness = epaisseur ?? DEFAULT_THICKNESS[wallType];
    if (thickness < THICKNESS_CM.min || thickness > THICKNESS_CM.max) {
      throw new Refus(`Une épaisseur de ${thickness} cm n'est pas possible : de ${THICKNESS_CM.min} à ${THICKNESS_CM.max} cm.`);
    }
    etapes.push({ type: 'murs', a: toPx(a), b: toPx(b), wallType, thickness });
    segments.push({ kind: 'ajout', a: toPx(a), b: toPx(b), label: meters(len) });
    return { len, thickness };
  };

  const describeWalls = (built: { len: number; thickness: number }[], wallType: 'exterior' | 'partition') => {
    const noun = wallType === 'partition' ? ['une cloison', 'cloisons', 'cloison'] : ['un mur extérieur', 'murs extérieurs', 'mur extérieur'];
    const thick = `épaisseur ${built[0].thickness} cm`;
    if (built.length === 1) {
      setTitre(`${noun[2]} de ${meters(built[0].len)}`);
      return `Ajouter ${noun[0]} de ${meters(built[0].len)}, ${thick}`;
    }
    setTitre(`${built.length} ${noun[1]}`);
    const lens = built.map(b => meters(b.len));
    return `Ajouter ${built.length} ${noun[1]} (${lens.slice(0, -1).join(', ')} et ${lens.at(-1)}), ${thick}`;
  };

  /** Le cote est-il deja couvert par des murs de l'etage ? Des murs dans son axe, sur 95 % de sa longueur */
  const sideCovered = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    const along = (q: Point) => (toCm(q.x) - a.x) * ux + (toCm(q.y) - a.y) * uy;
    const off = (q: Point) => Math.abs((toCm(q.x) - a.x) * uy - (toCm(q.y) - a.y) * ux);
    const spans = walls
      .filter(w => off(w.start) < SIDE_TOLERANCE_CM && off(w.end) < SIDE_TOLERANCE_CM)
      .map(w => [Math.min(along(w.start), along(w.end)), Math.max(along(w.start), along(w.end))] as const)
      .map(([s, e]) => [Math.max(0, s), Math.min(len, e)] as const)
      .filter(([s, e]) => e > s)
      .sort((u, v) => u[0] - v[0]);
    let covered = 0;
    let end = 0;
    for (const [s, e] of spans) {
      if (e <= end) continue;
      covered += e - Math.max(s, end);
      end = e;
    }
    return covered / len >= SIDE_COVERED;
  };

  try {
    for (const action of actions) {
      switch (action.outil) {
        case 'ajouter_murs': {
          const groups = new Map<string, { wallType: 'exterior' | 'partition'; built: { len: number; thickness: number }[] }>();
          for (const m of action.arguments.murs) {
            const wallType = m.type === 'cloison' ? 'partition' : 'exterior';
            const built = addWall(m.depart, m.arrivee, wallType, m.epaisseur);
            if (!built) continue;
            const key = `${wallType}/${built.thickness}`;
            if (!groups.has(key)) groups.set(key, { wallType, built: [] });
            groups.get(key)!.built.push(built);
          }
          for (const g of groups.values()) descriptions.push(describeWalls(g.built, g.wallType));
          break;
        }
        case 'creer_piece': {
          const { nom, contour } = action.arguments;
          const area = Math.abs(contour.reduce((sum, q, i) => {
            const n = contour[(i + 1) % contour.length];
            return sum + q.x * n.y - n.x * q.y;
          }, 0)) / 2 / 10000;
          if (area < MIN_ROOM_M2) throw new Refus(`Une pièce de ${area.toFixed(1).replace('.', ',')} m² est trop petite : ${MIN_ROOM_M2} m² au moins.`);
          const built = contour.flatMap((a, i) => {
            const b = contour[(i + 1) % contour.length];
            const wall = sideCovered(a, b) ? null : addWall(a, b, 'partition');
            return wall ? [wall] : [];
          });
          if (built.length > 0) descriptions.push(describeWalls(built, 'partition'));
          const center = {
            x: contour.reduce((s, q) => s + q.x, 0) / contour.length,
            y: contour.reduce((s, q) => s + q.y, 0) / contour.length,
          };
          etapes.push({ type: 'piece', nom, point: toPx(center) });
          textes.push({ at: toPx(center), text: nom });
          descriptions.push(`Nommer la pièce « ${nom} »`);
          titre = nom;
          break;
        }
        case 'poser_ouverture': {
          const { mur, type, position, largeur } = action.arguments;
          const wall = wallOf(mur);
          const kind = STYLES[type];
          const entry = WINDOW_CATALOG[kind.style];
          const width = largeur ?? entry.defaultWidth;
          if (deleted.has(wall.id)) throw new Refus(`Le mur ${mur} est supprimé par cette même proposition.`);
          if (width < OPENING_WIDTH_CM.min) throw new Refus(`Une largeur de ${width} cm n'est pas possible : ${OPENING_WIDTH_CM.min} cm au moins.`);
          const wallLen = toCm(Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y));
          const center = position * wallLen;
          if (center - width / 2 < 0 || center + width / 2 > wallLen) {
            throw new Refus(`Une ${kind.name} de ${width} cm ne tient pas à cet endroit du mur ${mur}, qui mesure ${meters(wallLen)}.`);
          }
          const other = p.openings.find(o => o.wallId === wall.id
            && Math.abs(o.position * wallLen - center) < (o.width + width) / 2);
          if (other || proposed.some(o => o.wallId === wall.id && Math.abs(o.center - center) < (o.width + width) / 2)) {
            throw new Refus(`Une ${kind.name} à cet endroit chevauche une ouverture du mur ${mur}.`);
          }
          if (width > OPENING_WIDTH_CM.max) throw new Refus(`Une largeur de ${width} cm n'est pas possible : ${OPENING_WIDTH_CM.max} cm au plus.`);
          proposed.push({ wallId: wall.id, center, width });
          openedWalls.add(wall.id);
          const at = (cm: number): Point => ({
            x: wall.start.x + (wall.end.x - wall.start.x) * (cm / wallLen),
            y: wall.start.y + (wall.end.y - wall.start.y) * (cm / wallLen),
          });
          etapes.push({
            type: 'ouverture',
            opening: {
              type: kind.type, roomId: '', wallIndex: 0, wallId: wall.id, position, width,
              height: entry.defaultHeight, sillHeight: entry.defaultSillHeight, style: kind.style, leaves: entry.defaultLeaves,
              openDirection: 1, hingeSide: 1, floorId: etageId,
            },
          });
          segments.push({ kind: 'ajout', a: at(center - width / 2), b: at(center + width / 2), label: `${kind.name[0].toUpperCase()}${kind.name.slice(1)} ${width} cm` });
          descriptions.push(`Poser une ${kind.name} de ${width} cm`);
          setTitre(`${kind.name} de ${width} cm`);
          break;
        }
        case 'supprimer_murs': {
          const targets = action.arguments.murs.map(id => ({ id, wall: wallOf(id) }));
          const locked = targets.find(t => t.wall.locked);
          if (locked) throw new Refus(`Le mur ${locked.id} est verrouillé : il faut d'abord le déverrouiller.`);
          const opened = targets.find(t => openedWalls.has(t.wall.id));
          if (opened) throw new Refus(`Le mur ${opened.id} est supprimé par cette même proposition, qui y pose une ouverture.`);
          targets.forEach(t => deleted.add(t.wall.id));
          etapes.push({ type: 'supprimer', ids: targets.map(t => t.wall.id) });
          for (const { wall } of targets) segments.push({ kind: 'suppression', a: wall.start, b: wall.end });
          const n = targets.length;
          descriptions.push(n === 1 ? 'Supprimer 1 mur' : `Supprimer ${n} murs`);
          setTitre(n === 1 ? 'supprimer 1 mur' : `supprimer ${n} murs`);
          break;
        }
        case 'demander_endroit':
          // Pas une action sur le plan : l'onglet le traite avant (COP-E04)
          throw new Refus('Montrer l\'endroit n\'est pas une action sur le plan.');
      }
    }
  } catch (err) {
    if (err instanceof Refus) return { ok: false, raison: err.message };
    throw err;
  }

  // Les ouvertures d'abord : quand un nouveau mur coupe un mur, l'editeur deplace les ouvertures existantes, et la
  // position verifiee ici reste celle du mur entier
  const rank = (e: Etape) => (e.type === 'ouverture' ? 0 : e.type === 'piece' ? 2 : 1);
  etapes.sort((u, v) => rank(u) - rank(v));

  return {
    ok: true, etapes, descriptions, titre, nombre: descriptions.length,
    apercu: { segments, textes },
    boutonSupprimer: etapes.every(e => e.type === 'supprimer'),
  };
}
